/**
 * BookKitPreview — bundles Publisher's Kit management + Live Preview for the
 * Upload Draft view (BookEditor.jsx). The Live Preview embeds the exact PDF
 * that ``/api/books/{id}/export/pdf`` would produce, served with
 * ``Content-Disposition: inline`` from ``/api/books/{id}/preview/pdf``. This
 * guarantees byte-identical parity between preview and the file the user
 * downloads — no hand-rolled HTML renderer, no font drift, no page-break
 * drift.
 *
 * Consumes and mutates the parent `book` document via `setBook` (client-side
 * state) — server writes happen through the /preset-kits/{id}/apply endpoint
 * which returns the full book payload.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Eye, X, RefreshCw, Download } from "lucide-react";
import { api, API, formatApiError } from "@/lib/api";
import { PublishersKitBar } from "@/components/manual/PublishersKit";
import { PdfPreview } from "@/components/manual/PdfPreview";

const DEFAULT_STYLE_PACK = "classic_trade";

export function BookKitPreview({ book, setBook, stylePack }) {
  const [presets, setPresets] = useState([]);
  const [kits, setKits] = useState([]);
  const [team, setTeam] = useState(null);
  // iter-99 — workspaces so the "Share with…" scope dropdown includes
  // every workspace the caller can share into.
  const [workspaces, setWorkspaces] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [previewNonce, setPreviewNonce] = useState(0);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [stylePacks, setStylePacks] = useState([]);
  const [previewStylePack, setPreviewStylePack] = useState(stylePack || DEFAULT_STYLE_PACK);

  const loadPresets = useCallback(async () => {
    try {
      const { data } = await api.get("/manual-presets");
      setPresets(data || []);
    } catch (err) { console.warn("loadPresets failed:", err?.message || err); }
  }, []);
  const loadKits = useCallback(async () => {
    try {
      const { data } = await api.get("/preset-kits");
      setKits(data || []);
    } catch (err) { console.warn("loadKits failed:", err?.message || err); }
  }, []);
  const loadTeam = useCallback(async () => {
    try {
      const { data } = await api.get("/ph360/team");
      setTeam(data?.team || null);
    } catch { setTeam(null); }
  }, []);
  const loadWorkspaces = useCallback(async () => {
    try {
      const { data } = await api.get("/workspaces/mine");
      const list = Array.isArray(data) ? data : (data?.workspaces || []);
      setWorkspaces(list);
    } catch { setWorkspaces([]); }
  }, []);
  const loadStylePacks = useCallback(async () => {
    try {
      const { data } = await api.get("/formatter-v2/style-packs");
      setStylePacks(data?.style_packs || []);
    } catch (err) { console.debug("style-pack list load failed:", err?.message || err); }
  }, []);
  useEffect(() => { loadPresets(); loadKits(); loadTeam(); loadWorkspaces(); loadStylePacks(); },
    [loadPresets, loadKits, loadTeam, loadWorkspaces, loadStylePacks]);

  // Keep the preview's style pack synced with the export selector whenever
  // the modal is not open — while it IS open, the user may want to compare
  // packs independently, so we don't overwrite their local choice.
  useEffect(() => {
    if (!showPreview && stylePack) setPreviewStylePack(stylePack);
  }, [stylePack, showPreview]);

  const applyKit = useCallback(async (kitId) => {
    try {
      const { data } = await api.post(`/preset-kits/${kitId}/apply/${book.id}`);
      setBook(data);
      toast.success("Kit applied");
    } catch (err) { toast.error(formatApiError(err)); }
  }, [book.id, setBook]);

  const createKit = useCallback(async (payload) => {
    try {
      const { data } = await api.post("/preset-kits", payload);
      setKits((prev) => [data, ...prev]);
      toast.success(`Kit saved: ${data.name}`);
      return data;
    } catch (err) { toast.error(formatApiError(err)); return null; }
  }, []);

  const updateKit = useCallback(async (kitId, payload) => {
    try {
      const { data } = await api.patch(`/preset-kits/${kitId}`, payload);
      setKits((prev) => prev.map((k) => k.id === kitId ? data : k));
      toast.success(`Kit updated: ${data.name}`);
      return data;
    } catch (err) { toast.error(formatApiError(err)); return null; }
  }, []);

  const deleteKit = useCallback(async (kitId) => {
    if (!window.confirm("Delete this Publisher's Kit? Its individual presets stay saved.")) return;
    try {
      await api.delete(`/preset-kits/${kitId}`);
      setKits((prev) => prev.filter((k) => k.id !== kitId));
      toast.success("Kit deleted");
    } catch (err) { toast.error(formatApiError(err)); }
  }, []);

  // Book has renderable content if the formatter_v2 pipeline has produced HTML,
  // or if the user has any chapters / intro / front / back matter. The
  // preview endpoint mirrors ``_build_pdf_bytes_for_book`` on the backend.
  const hasContent = useMemo(() => {
    if ((book.formatter_v2_html || "").trim()) return true;
    if ((book.chapters || []).length > 0) return true;
    if ((book.introduction || "").trim()) return true;
    if ((book.front_matter_sections || []).some((s) => s.include && (s.content || "").trim())) return true;
    if ((book.back_matter_sections || []).some((s) => s.include && (s.content || "").trim())) return true;
    return false;
  }, [book]);

  const previewSrc = useMemo(() => {
    if (!book.id) return "";
    const qs = new URLSearchParams({ style_pack: previewStylePack, v: String(previewNonce) });
    // toolbar/navpanes hints are Chrome PDF viewer hints; harmless elsewhere.
    return `${API}/books/${book.id}/preview/pdf?${qs.toString()}#toolbar=1&navpanes=0`;
  }, [book.id, previewStylePack, previewNonce]);

  // iter-107: auto-refresh the preview iframe whenever the persisted
  // book state changes. Without this, an author who saves a manual
  // edit while the preview is open keeps staring at the pre-save PDF
  // until they explicitly click "Refresh preview" — which reads like
  // "my changes didn't take effect" and is the #1 support ping we've
  // seen after the auto-save loop was fixed. Fingerprints only the
  // fields the preview actually renders so cosmetic reloads (e.g.,
  // hero-media state, workspace_ids) don't bounce the iframe.
  const previewFingerprint = useMemo(() => {
    if (!book || !book.id) return "";
    return JSON.stringify({
      t: book.title, s: book.subtitle, a: book.author, p: book.publisher,
      c: (book.chapters || []).map((ch) => [
        ch.title || "", ch.subtitle || "", ch.description || "",
        (ch.content || "").length, ch.kind || "", ch.label || "",
      ]),
      i: book.introduction || "", n: book.toc_note || "",
      f: (book.front_matter_sections || []).map((x) => [x.key, x.include, (x.content || "").length]),
      b: (book.back_matter_sections || []).map((x) => [x.key, x.include, (x.content || "").length]),
      num: book.chapter_numbering_style || "arabic",
      v2: (book.formatter_v2_html || "").length,
      cov: (book.cover_data_url || "").length,
      fp: book.cover_focal_point || null,
    });
  }, [book]);
  useEffect(() => {
    if (!showPreview) return;
    // Bump only after the initial mount effect that opened the preview.
    setPreviewNonce((n) => n + 1);
    setPreviewLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewFingerprint]);

  const openPreview = useCallback(() => {
    setPreviewLoading(true);
    setPreviewNonce((n) => n + 1);
    setShowPreview(true);
  }, []);

  const refreshPreview = useCallback(() => {
    setPreviewLoading(true);
    setPreviewNonce((n) => n + 1);
  }, []);

  return (
    <div className="mb-6 space-y-3" data-testid="book-kit-preview-wrap">
      <div className="flex items-stretch gap-3">
        <div className="flex-1">
          <PublishersKitBar
            kits={kits} presets={presets} team={team} workspaces={workspaces}
            currentTocNote={book.toc_note || ""}
            currentIntroduction={book.introduction || ""}
            onApplyKit={applyKit} onDeleteKit={deleteKit}
            onCreateKit={createKit} onUpdateKit={updateKit}
          />
        </div>
        <button
          type="button"
          onClick={openPreview}
          className="text-sm inline-flex items-center gap-1 px-3 py-1 border border-[color:var(--border)] hover:bg-[color:var(--canvas)] bg-white self-center"
          data-testid="book-preview-open"
          title="Open a print-accurate live preview of the exported PDF"
        >
          <Eye className="w-4 h-4"/> Preview
        </button>
      </div>

      {showPreview && (
        <div className="fixed inset-0 z-50 bg-black/60 flex" data-testid="book-preview-modal"
             onClick={(e) => { if (e.target === e.currentTarget) setShowPreview(false); }}>
          <div className="ml-auto w-full max-w-5xl bg-[color:var(--canvas)] shadow-2xl border-l border-[color:var(--border)] flex flex-col">
            <div className="sticky top-0 bg-white border-b border-[color:var(--border)] px-6 py-3 flex items-center justify-between gap-3 z-10">
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-[0.25em] text-[color:var(--text-muted)]">§ Live preview</p>
                <p className="text-xs text-[color:var(--text-muted)] truncate">
                  Byte-identical to the exported PDF — same style pack, fonts, pagination and running headers.
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {stylePacks.length > 0 && (
                  <label className="text-xs flex items-center gap-1">
                    <span className="uppercase tracking-widest text-[color:var(--text-muted)]">Style</span>
                    <select
                      value={previewStylePack}
                      onChange={(e) => { setPreviewStylePack(e.target.value); setPreviewLoading(true); setPreviewNonce((n) => n + 1); }}
                      className="text-sm border border-[color:var(--border)] bg-white px-2 py-1"
                      data-testid="book-preview-style-pack"
                    >
                      {stylePacks.map((p) => (
                        <option key={p.key} value={p.key}>{p.name || p.key}</option>
                      ))}
                    </select>
                  </label>
                )}
                <button onClick={refreshPreview}
                  className="btn-ghost text-sm inline-flex items-center gap-1"
                  data-testid="book-preview-refresh"
                  title="Re-render with latest edits">
                  <RefreshCw className="w-4 h-4"/> Refresh
                </button>
                <a href={previewSrc.replace(/#.*$/, "")} target="_blank" rel="noreferrer"
                  className="btn-ghost text-sm inline-flex items-center gap-1"
                  data-testid="book-preview-open-tab"
                  title="Open the preview PDF in a new tab">
                  <Download className="w-4 h-4"/> Open
                </a>
                <button onClick={() => setShowPreview(false)}
                  className="btn-ghost text-sm inline-flex items-center gap-1"
                  data-testid="book-preview-close">
                  <X className="w-4 h-4"/> Close
                </button>
              </div>
            </div>
            <div className="flex-1 relative bg-[color:var(--canvas)]">
              {!hasContent ? (
                <div className="p-10 text-center text-sm text-[color:var(--text-muted)]" data-testid="book-preview-empty">
                  No content yet — upload a manuscript or add chapters to see the live preview.
                </div>
              ) : (
                <PdfPreview
                  url={previewSrc.replace(/#.*$/, "")}
                  cacheKey={`${previewStylePack}:${previewNonce}`}
                  onLoaded={() => setPreviewLoading(false)}
                  className="absolute inset-0"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
